import React, { useState } from 'react'
import CustomRow from '../../../../reusable-components/CustomRow'
import { Button, Col, DatePicker, Input, Space, Table, Typography, Modal, theme } from 'antd'
import { hexToRGBA } from '../../../../lib/utils'
import SystemUsersTemplates from '../../../../Admin/system-users/utils/templates'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faUserPlus, faSearch } from '@fortawesome/free-solid-svg-icons'
import { DataComplianceTableTemplates } from './utils/templates'
import useEnrollmentSearch from './hooks/useEnrollmentSearch'
import dayjs from 'dayjs'

const { Title, Text } = Typography

const EnrollmentSearch = ({ template, setModalOpen, onAfterSuccess, isMonitoring }) => {
  const {
    setSearchQuery,
    patient,
    setPatient,
    searchResults,
    handleEnrollPatient,
    searching,
    enrolling,
    enrollmentOverride,
    setEnrollmentOverride,
    handleTeamMemberSelection,
    teamMembers,
    handlePatientQuery,
    handleReset
  } = useEnrollmentSearch({ template, setModalOpen, onAfterSuccess })

  const [showDateModal, setShowDateModal] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)

  const { token } = theme.useToken()

  const handleEnrollClick = (record) => {
    setSelectedPatient(record)
    setShowDateModal(true)
  }

  const confirmEnrollment = () => {
    setPatient(selectedPatient)
    handleEnrollPatient()
    setShowDateModal(false)
  }

  // Custom columns for the patient search table
  const patientColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (record) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'ID Number',
      dataIndex: 'idNo',
      key: 'idNo'
    },
    {
      title: 'Contact',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber) =>
        phoneNumber ? (phoneNumber.startsWith('+27') ? `0${phoneNumber.slice(3)}` : phoneNumber) : 'Not provided'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Button
          type='primary'
          icon={<FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 8 }} />}
          onClick={(e) => {
            e.stopPropagation()
            handleEnrollClick(record)
          }}
          style={{
            background: 'rgba(56, 158, 13, 0.1)',
            borderColor: '#389E0D',
            color: '#389E0D',
            height: '32px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(56, 158, 13, 0.2)'
            e.currentTarget.style.transform = 'translateY(-1px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(56, 158, 13, 0.1)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          Enroll
        </Button>
      )
    }
  ]

  return (
    <>
      <div
        style={{
          width: '100%',
          padding: '8px 12px',
          background: hexToRGBA(token.colorPrimary, 0.2),
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          top: 0,
          left: 0
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          New Enrollment
        </Title>
        <Button
          style={{
            background: 'transparent',
            border: 'none',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
          onClick={() => handleReset()}
        >
          <FontAwesomeIcon
            icon={faXmark}
            style={{
              fontSize: '24px',
              color: token.colorTextSecondary
            }}
          />
        </Button>
      </div>

      <CustomRow style={{ marginTop: 48 }}>
        <Col span={24} style={{ marginBottom: 16 }}>
          <Input
            suffix={<FontAwesomeIcon icon={faSearch} style={{ color: token.colorTextSecondary }} />}
            placeholder='Search patients to enroll'
            onChange={(e) => handlePatientQuery(e.target.value)}
            style={{ width: '400px', maxWidth: '100%' }}
            size='middle'
          />
        </Col>

        <Col span={24}>
          <Table
            size='small'
            bordered
            loading={searching}
            dataSource={searchResults}
            columns={patientColumns}
            rowKey={(record) => record._id}
            pagination={{ pageSize: 10 }}
          />
        </Col>

        <Col span={24}>
          <Space style={{ float: 'right', marginTop: 12 }}>
            <Button
              disabled={enrolling || searching}
              onClick={handleReset}
              style={{
                height: '36px',
                borderRadius: '6px',
                padding: '4px 20px',
                background: 'rgba(233, 30, 99, 0.1)',
                borderColor: '#E91E63',
                color: '#E91E63'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(233, 30, 99, 0.2)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(233, 30, 99, 0.1)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              Cancel
            </Button>
          </Space>
        </Col>
      </CustomRow>

      <Modal
        title={
          <div
            style={{
              padding: '16px 24px',
              margin: '-20px -24px 20px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FontAwesomeIcon icon={faUserPlus} style={{ color: '#389E0D', fontSize: '16px' }} />
            <span style={{ fontWeight: 600, fontSize: '16px' }}>Confirm Enrollment</span>
          </div>
        }
        open={showDateModal}
        onCancel={() => setShowDateModal(false)}
        onOk={confirmEnrollment}
        okText='Enroll'
        width={400}
        className='enrollment-confirm-modal'
        style={{ maxWidth: '400px' }}
        bodyStyle={{
          padding: '0 24px 24px'
        }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '12px 24px',
              marginTop: '24px',
              borderTop: '1px solid #f0f0f0'
            }}
          >
            <Button
              onClick={() => setShowDateModal(false)}
              style={{
                height: '36px',
                borderRadius: '6px',
                padding: '4px 20px',
                background: 'rgba(233, 30, 99, 0.1)',
                borderColor: '#E91E63',
                color: '#E91E63'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(233, 30, 99, 0.2)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(233, 30, 99, 0.1)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={confirmEnrollment}
              loading={enrolling}
              style={{
                background: 'rgba(56, 158, 13, 0.1)',
                borderColor: '#389E0D',
                color: '#389E0D',
                height: '36px',
                borderRadius: '6px',
                padding: '4px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(56, 158, 13, 0.2)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(56, 158, 13, 0.1)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              Enroll
            </Button>
          </div>
        }
      >
        <div style={{ padding: '0' }}>
          <p
            style={{
              fontSize: '14px',
              color: token.colorTextSecondary,
              marginBottom: '20px'
            }}
          >
            You are about to enroll{' '}
            <strong style={{ color: token.colorText }}>
              {selectedPatient?.firstName} {selectedPatient?.lastName}
            </strong>{' '}
            in <strong style={{ color: token.colorText }}>{template.name}</strong>
          </p>
          <div>
            <span
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: token.colorTextSecondary
              }}
            >
              Enrollment Date:
            </span>
            <DatePicker
              style={{
                width: '100%',
                height: '36px',
                borderRadius: '6px'
              }}
              allowClear={false}
              onChange={(date) => setEnrollmentOverride(date)}
              value={enrollmentOverride}
              format='DD-MM-YYYY'
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default EnrollmentSearch
